import { YYYY_MM_DD } from "@/features/event-details/_logic/date-utils";
import { formatAgendaDateLong } from "@/features/event-details-agenda/_logic/date-format";
import type { FoodBeverageData, FoodBeverageMeals } from "../_types/wizard-types";
import { FOOD_BEVERAGE_MEAL_OPTIONS } from "./constants";

/**
 * Narrows an untrusted meal selection (client state or draft jsonb) to the allowlisted services
 * and well-formed, sorted, de-duplicated days.
 *
 * @param raw - Raw selection value.
 * @returns The sanitized selection; `{}` when nothing valid is present.
 */
export function sanitizeFoodBeverageMeals(raw: unknown): FoodBeverageMeals {
  if (!raw || typeof raw !== "object") return {};
  const value = raw as Record<string, unknown>;
  const meals: FoodBeverageMeals = {};
  for (const meal of FOOD_BEVERAGE_MEAL_OPTIONS) {
    const days = value[meal];
    if (!Array.isArray(days)) continue;
    meals[meal] = [...new Set(days.filter((d): d is string => typeof d === "string" && YYYY_MM_DD.test(d)))].sort();
  }
  return meals;
}

/**
 * Folds the selected services (with their days) and the free-text detail into the single
 * `events.food_beverage_requirements` string venues read, e.g.
 * `"Coffee & tea; Lunch: Wed Sep 23, Thu Sep 24. Vegetarian options please"`.
 *
 * @param foodBeverage - The Food & beverage step's answer.
 * @returns The combined text, or `""` when neither selections nor detail were given.
 */
export function formatFoodBeverageRequirements(foodBeverage: FoodBeverageData): string {
  const meals = sanitizeFoodBeverageMeals(foodBeverage.meals);
  const selections = FOOD_BEVERAGE_MEAL_OPTIONS.filter((meal) => meals[meal])
    .map((meal) => {
      const days = meals[meal] ?? [];
      if (days.length === 0) return meal;
      return `${meal}: ${days.map((d) => formatAgendaDateLong(d).replace(",", "")).join(", ")}`;
    })
    .join("; ");
  const details = foodBeverage.details.trim();
  return [selections, details].filter(Boolean).join(". ");
}
